import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { JsonLd, useSeo } from "../components/marketing/Seo";
import Breadcrumbs from "../components/marketing/Breadcrumbs";
import { IMAGES, SAMPLE_BRIEFINGS } from "../data/marketing";
import { readMinutes } from "./InsightsPage";

const API = process.env.REACT_APP_BACKEND_URL;

function renderInline(text) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold text-forest-deep">{part}</strong> : part));
}

function ArticleBody({ body }) {
  const blocks = body.split(/\n\n+/);
  const out = [];
  let list = [];
  const flushList = (key) => {
    if (list.length) {
      out.push(
        <ul key={key} className="mt-4 space-y-2.5">
          {list.map((li, j) => (
            <li key={j} className="flex items-start gap-3 leading-relaxed text-forest-deep/80">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-forest-primary" />
              <span>{renderInline(li)}</span>
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  blocks.forEach((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return;
    if (trimmed.startsWith("## ")) {
      flushList(`l${i}`);
      out.push(<h2 key={i} className="mt-10 font-display text-2xl font-medium tracking-tight text-forest-deep">{trimmed.slice(3)}</h2>);
    } else if (trimmed.startsWith("- ")) {
      trimmed.split("\n").forEach((line) => line.trim().startsWith("- ") && list.push(line.trim().slice(2)));
    } else {
      flushList(`l${i}`);
      out.push(<p key={i} className="mt-5 leading-relaxed text-forest-deep/80">{renderInline(trimmed)}</p>);
    }
  });
  flushList("last");
  return <div data-testid="article-body">{out}</div>;
}

function ArticleSeo({ article }) {
  const image = article.image?.startsWith("/") ? `https://clymaco.com${article.image}` : article.image;
  useSeo({ title: article.title, description: article.excerpt, path: `/insights/${article.slug}`, image });
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: { "@type": "Organization", name: article.author || "CLYMACO" },
    publisher: { "@type": "Organization", name: "CLYMACO", logo: { "@type": "ImageObject", url: "https://clymaco.com/logo-full.png" } },
    datePublished: article.published_at || (article.draft ? "2026-09-25" : undefined),
    dateModified: article.updated_at || article.published_at || (article.draft ? "2026-09-25" : undefined),
    mainEntityOfPage: `https://clymaco.com/insights/${article.slug}`,
  };
  return <JsonLd data={schema} id={`article-schema-${article.slug}`} />;
}

export default function ArticlePage() {
  const { slug } = useParams();
  const sample = SAMPLE_BRIEFINGS.find((a) => a.slug === slug);
  const [article, setArticle] = useState(sample || null);
  const [others, setOthers] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
    window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    if (sample) {
      setArticle(sample);
    } else {
      setArticle(null);
      fetch(`${API}/api/articles/${slug}`)
        .then((r) => {
          if (!r.ok) throw new Error("not found");
          return r.json();
        })
        .then((a) => setArticle({ ...a, author: "CLYMACO", category: "EPR", image: IMAGES.hero }))
        .catch(() => setMissing(true));
    }
    fetch(`${API}/api/articles`)
      .then((r) => r.json())
      .then((d) => {
        const published = (d.items || []).filter((a) => a.slug !== slug).map((a) => ({ ...a, author: "CLYMACO", image: IMAGES.hero }));
        setOthers([...SAMPLE_BRIEFINGS.filter((a) => a.slug !== slug), ...published].slice(0, 3));
      })
      .catch(() => setOthers(SAMPLE_BRIEFINGS.filter((a) => a.slug !== slug)));
  }, [slug, sample]);

  return (
    <main id="main" className="bg-parchment-base pt-36" data-testid="article-page">
      {article && <ArticleSeo article={article} />}
      <section className="mx-auto max-w-4xl px-4 sm:px-6">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Insights", href: "/insights" }, { label: article?.title || "Article" }]} />
        <Link to="/insights" className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-forest-primary" data-testid="article-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to insights
        </Link>
        {article && (
          <div className="mt-8">
            <div className="flex flex-wrap gap-2">
              <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${article.draft ? "bg-amber-500/15 text-amber-800" : "bg-emerald-500/10 text-emerald-800"}`}>
                {article.draft ? "Sample briefing · Draft for review" : "Published article"}
              </span>
              <span className="rounded-full bg-forest-deep/5 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-forest-deep/60">{article.category}</span>
            </div>
            <h1 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight text-forest-deep sm:text-5xl" data-testid="article-title">{article.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-forest-deep/55">
              <span>{article.author || "CLYMACO"}</span>
              <span>{article.date || (article.published_at ? new Date(article.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "")}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{readMinutes(article.body)} min read</span>
            </div>
            {article.draft && <p className="mt-5 rounded-2xl bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-900" data-testid="article-draft-note">This is sample editorial copy attached for review. It is clearly marked as a draft until approved for publication.</p>}
          </div>
        )}
        {missing && <h1 className="mt-8 font-display text-3xl font-medium text-forest-deep" data-testid="article-missing">Article not found.</h1>}
      </section>

      {article && (
        <>
          <section className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
            <img src={article.image || IMAGES.hero} alt={article.draft ? "Illustrative article image" : "CLYMACO editorial image"} className="h-[360px] w-full rounded-[2rem] object-cover" />
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-forest-deep/45">Illustrative brand imagery</p>
          </section>
          <article className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
            <ArticleBody body={article.body} />
          </article>
          <section className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
            <div className="rounded-3xl bg-forest-deep p-8 text-[#FBF9F4] sm:p-10" data-testid="article-cta">
              <h2 className="font-display text-2xl font-medium">Need this applied to your organisation?</h2>
              <p className="mt-3 text-sm leading-relaxed text-emerald-100/70">Tell us which obligation, site, programme or market you are reviewing. We will respond with an appropriate scope.</p>
              <Link to={`/contact?topic=${encodeURIComponent(article.category || "A mandate across several practices")}`} className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 font-display text-sm font-bold text-forest-deep transition-colors hover:bg-emerald-300" data-testid="article-cta-button">
                Start a conversation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>
          {others.length > 0 && (
            <section className="mx-auto max-w-4xl px-4 pb-24 sm:px-6">
              <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-forest-primary">Related reading</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {others.map((item) => (
                  <Link key={item.slug} to={`/insights/${item.slug}`} className="rounded-2xl border border-forest-deep/10 bg-white p-5 transition-all hover:border-forest-primary/30 hover:shadow-md" data-testid={`article-more-${item.slug}`}>
                    <span className={`rounded-full px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-widest ${item.draft ? "bg-amber-500/15 text-amber-800" : "bg-emerald-500/10 text-emerald-800"}`}>{item.draft ? "Draft" : "Published"}</span>
                    <h3 className="mt-3 font-display text-base font-medium leading-snug text-forest-deep">{item.title}</h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
